import { Input } from "src/components/input";
import Slider from "src/components/slider";
import styles from "./createStream.module.scss";
import React, { ChangeEvent, useEffect, useState } from "react";
import Button from "src/components/button";
import { useSelector } from "react-redux";
import Dropdown from 'react-bootstrap/Dropdown';
import cx from "classnames";
import { useDispatch } from "react-redux";
import { createStream, invalidateStreamCreation } from "src/redux/actions/hub";
import Thumbnail from "src/components/thumbnail";
import { EditThumbnail } from "src/components/editThumbnail";
import { MediaLibraryChooser } from "src/components/mediaLibrary";
import { nanoid } from '@reduxjs/toolkit';
import _ from 'lodash';
import { MediaFile } from "src/types/media";
import { MediaLibrary } from "src/types/mediaLibrary";
import { animated, useTransition } from '@react-spring/web';
import Actions from "src/components/actionbar";
import { POSITIONS } from "src/constants/banner";
import { parseMediaMetadata } from "src/utils/media.service";
import { Crop } from "src/components/editThumbnail/crop";
import { uploadMedia } from "src/redux/actions/media";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";
import { useNavigate } from "react-router-dom";
import Reveal from "src/components/animation";
import { Stream } from "src/types/streams";
import { useHubAdoption } from "src/redux/actions/adoption/hub";
import { useStreamsAdoption } from "src/redux/actions/adoption/streams";
import { toast } from "react-toastify";

interface CreateStreamProps {
  show: boolean;
  onClose: () => void;
}

export const CreateStream: React.FC<CreateStreamProps> = ({ show, onClose }) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { 
    trackStreamAdded, 
    trackStreamCreated, 
    trackAddBannerClicked, 
    trackCropBannerClicked, 
    trackBannerContentSelected,
    trackBannerCropped,
    trackBannerAdded
    } = useStreamsAdoption();

  const [name, setName] = useState<string>("");
  const [sections, setSections] = useState<{ label: string, value: string } []>([]);
  const [selectedSection, setSelectedSection] = useState<{ label: string, value: string } | null>(null);
  const [summary, setSummary] = useState<string>("");
  const [editAction, setEditAction] = useState("");
  const [cropData, setCropData] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [position, setPosition] = useState("center center");
  const [mediaType, setMediaType] = useState("image");
  const [stream, setStream] = useState<Stream>({} as Stream);
  const [error, setError] = useState<string>("");

  const rows = useSelector((state: any) => _.get(state, "hub.item.rows"));
  const creating = useSelector((state: any) => state.stream.creation.creating);
  const created = useSelector((state: any) => state.stream.creation.created);
  const item = useSelector((state: any) => state.stream.creation.item);
  const creationError = useSelector((state: any) => state.stream.creation.error);

  const uploadedBackground = useSelector((state: any) => {
    const background = stream?.banner?.background;
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background?.id}'].item`);
    }

    return false;
  });

  const uploading = useSelector((state: any) => {
    const background = stream?.banner?.background;
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background?.id}'].loading`, false);
    }

    return false;
  });


  const uploaded = useSelector((state: any) => {
    const background = stream?.banner?.background;
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background?.id}'].loaded`, false);
    }

    return false;
  });

  const createStreamProps: IUseFetchProps = {
    action: createStream,
    loading: creating,
    succeeded: created,
    response: item,
    error: creationError
  }

  const { handleFetch: createNewStream } = useFetch(createStreamProps);
  const { trackHubAddCoverImageClickedEvent, trackHubCoverImageAddedEvent, trackCreateStreamScreenView } = useHubAdoption();

  useEffect(() => {
    if(show){
      trackCreateStreamScreenView();
    }
  },[show])

  useEffect(() => {
    const columns = rows.map((row: any) => row.columns);
    const layoutSections = columns.flatMap((column: any) =>
      column.filter((item: any) => _.get(item, "section.entityType") === 'stream' && _.get(item, "section.userDefined"))
        .map((item: any) => item.section)
    );

    const options = layoutSections.map((section: any) => ({
      label: section.title,
      value: section.id
    }));

    setSections(options);
    setSelectedSection(options[0]);

  }, [rows])

  useEffect(() => {
    if (uploaded) {
      handleCreateStream();
    }
  }, [uploaded])

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }

  const handleSummaryChange = (e: any) => {
    setSummary(e.target.value);
  }
 
  const handleCreateStream = () => {
    const request = {
      name: name,
      summary: summary,
      section: selectedSection?.value,
      banner: uploadedBackground ? {
        background: uploadedBackground.id,
        crop: cropData,
        position: position
      } : null
    };

    createNewStream(request).then((response) => {
      navigate(`/streams/${response.id}`)
      dispatch(invalidateStreamCreation());
      trackStreamAdded({ streamName: response.name, streamDescription: response.summary });
      trackStreamCreated({streamName: response.name, isCoverImageAdded: !!uploadedBackground, streamDescription: response.summary});
      onClose();
    });
  }

  const uploadBanner = async () => {
    const media = stream?.banner?.background;

    const mediaFile = media as MediaFile;

    const blob = await fetch(media!.full!).then(r => r.blob());
    const file = new File([blob], 'banner.png', { type: mediaFile.contentType || blob.type });

    dispatch(uploadMedia({
      id: media!.id,
      media: {
        ...media,
        file: file
      } as MediaFile
    }));
  }

  const handleCreateClick = () => {
    if (editAction === "" && (!name || name.trim() === "")) {
      setError("Please provide a valid stream name");
      return;
    }

    if(name.length > 200){
      setError("Stream name cannot be greater than 200 characters");
      return;
    }

    setError("");

    switch (editAction) {
      case "banner":
        trackHubCoverImageAddedEvent();
        trackBannerAdded({bannerType: mediaType});
        setEditAction("");
        break;

      case "crop":
        setStream({
          ...(stream || {}),
          banner: {
            ...(stream?.banner || {}),
            crop: cropData
          }
        });
        setEditAction("banner");
        trackBannerCropped({bannerType: mediaType});
        break;

      case "position":
        setEditAction("banner");
        break;

      case "":
        if (!stream?.banner?.background) {
          handleCreateStream();
        } else {
          uploadBanner();
        }

        break;

      default:
        break;
    }
  }

  const handleCancelClick = () => {
    
    if (editAction === "position") {
      setPosition("center center");
      setEditAction("banner");
    }
    else if (editAction === "crop") {
      setCropData({ x: 0, y: 0, width: 0, height: 0 });
      setEditAction("banner");
    }
    else if (editAction === "banner") {
      setEditAction("");
      setStream({} as Stream);
    }
    else {
      setEditAction("");
      setName("");
      setSummary("");
      setStream({} as Stream);
      setSelectedSection(sections[0]);
      onClose();
    }
  }

  const handleThumbnailChange = (media: MediaLibrary) => {

    if (!media) {
      return;
    }

    setMediaType(media.mediaType);

    const imageUrl = media.imageUrl;
    if (!imageUrl) {
      return;
    }

    fetch(imageUrl)
      .then((response) => {
        return response.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);

        const backgroundMedia = {
          id: nanoid(6),
          entityType: 'media-preview',
          type: media.mediaType,
          full: url,
          thumbnail: url,
          width: media.width,
          height: media.height,
          contentType: blob.type,
        } as MediaFile

        setStream({
          ...stream,
          banner: {
            ...(stream?.banner || {}),
            background: backgroundMedia,
          }
        })

        setEditAction("banner");
        trackBannerContentSelected({bannerType: media.mediaType})

      }).catch(error => {
        console.error(error);
      });
  };

  const handleClick = (action: string) => {
    setEditAction(action);
    switch(action) {
      case "edit":
        trackAddBannerClicked();
        break;

      case "crop":
        trackCropBannerClicked({bannerType: mediaType})
        break;
    }
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Please upload a file of size less than 10MB");
      return;
    }

    const thumbnailMedia = await parseMediaMetadata(file);
    if (!thumbnailMedia) return;

    setEditAction("banner");

    setStream({
      ...stream,
      banner: {
        ...(stream?.banner || {}),
        background: thumbnailMedia
      }
    })
  }

  const handleCroppedThumbnail = (value: { x: number, y: number, width: number, height: number }) => {
    const { x, y, width, height } = value;
    setCropData({ x, y, width, height });
  };

  const handlePositionChange = (value: string) => {
    setPosition(value);
    setStream({
      ...stream,
      banner: {
        ...stream?.banner,
        position: value
      }
    });
  }

  const renderStreamName = () => {
    return (
      <Input
        autoFocus
        className={styles.input}
        placeholder="Add a stream name..."
        value={name}
        onChange={handleNameChange}
      />
    )
  }

  const renderErrors = () => {
    return (
      <Reveal show={!!error}>
        <div className={styles.error}>
          <i className="paperflite-danger"></i>
          {error}
        </div>
      </Reveal>
    )
  }

  const renderStreamIcon = () => {
    return (
      <div
        className={styles.streamBanner}
        onClick={() => {
          trackHubAddCoverImageClickedEvent();
          setEditAction("banner");
        }}
      >
        <div className={styles.streamThumbnailWrapper}>
          <i className="paperflite-edit"></i>
        </div>
        <Thumbnail
          defaultImage="/images/assets/default_asset.jpg"
          media={stream?.banner?.background}
          crop={stream?.banner?.crop}
          className={styles.thumbnail}
        />
      </div>
    );
  }

  const renderSectionOptions = () => {
    return (
      <Dropdown>
        <Dropdown.Toggle as="div" className={styles.dropDown}>
          <p className={styles.label}>{selectedSection?.label}</p>
          <i className="paperflite-dropdown" />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {sections.map((item, index) => {
            const isActive = selectedSection?.value === item.value;
            return (
              <Dropdown.Item
                key={index}
                onClick={() => setSelectedSection(item)}
              >
                <i
                  className={cx(
                    styles.selectIcon,
                    {
                      [styles.activeSelect]: isActive,
                    },
                    isActive
                      ? "fa-solid fa-check-circle"
                      : "paperflite-radio-button-unselected",
                  )}
                />
                <span className={styles.name}>{item.label}</span>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  const renderStreamDescription = () => {
    return (
      <textarea rows={5}
        placeholder="Type here to add a description"
        onChange={handleSummaryChange}
        value={summary}>
      </textarea>
    )
  }

  const accept = "image/*, video/*";
  const disabled = false;

  return (
    <>
      {show && (
        <Slider.Dialog show={true} width={editAction === "edit" ? `33.25rem` : `23.75rem`} onClose={() => onClose}>
          <Slider.Header
            title={<>
              {editAction === "edit" && (<i className={cx("paperflite-left-arrow", styles.backIcon)} onClick={() => setEditAction("banner")}></i>)}
              <span>{editAction === "edit" ? "make it pretty, please" : "create new stream"}</span>

              {editAction === "edit" && (<button className={cx(styles.fileUpload)}
                disabled={false}>
                <i className={cx(styles.icon, "paperflite-upload")}></i>
                <span className={styles.title}>upload</span>
                <input type="file" accept={accept} disabled={disabled} onChange={handleFileChange}></input>
              </button>)}
            </>}
            message={editAction !== "edit" ? "start organizing content for your team" : ""}
          />
          <Slider.Body>
            <div className={styles.container}>

              {editAction === "" && (
                <>
                  {renderStreamName()}
                  {renderErrors()}
                  {renderStreamIcon()}
                  {_.size(sections) > 0 && renderSectionOptions()}
                  {renderStreamDescription()}
                </>
              )}

              {(editAction === "banner" || editAction === "position") && (
                <EditThumbnail
                  show={true}
                  isTransition={false}
                  icon={stream?.banner || null}
                  entityType="stream"
                  handleClick={handleClick}
                  position={position}
                  showToolbar={editAction !== "position"}
                  mediaType={mediaType}
                />
              )}

              {editAction === "edit" && (
                <div>
                  <MediaLibraryChooser entityType="stream" onChange={handleThumbnailChange} columns={4} />
                </div>
              )}

              {editAction === "crop" && (
                <Crop
                  enabled={true}
                  icon={stream?.banner?.background?.full}
                  loading={false}
                  onCrop={handleCroppedThumbnail}
                  headerDimension={{
                    height: 0,
                    width: 0,
                  }}
                  cropperDimensions={{
                    width: '15rem',
                    height: '19.375rem',
                  }}
                />
              )}

              {(editAction === "position") && (
                <BannerPositionMenu handlePositionChange={handlePositionChange} />
              )}

            </div>
          </Slider.Body>

          {(editAction !== "edit") && (
            <Slider.Actions>
              <Button
                variant="primary"
                icon="paperflite-tick"
                text={uploading || creating ? "Creating..." : (editAction === "" ? "Create" : "Save")}
                onClick={handleCreateClick}
                disabled={uploading || creating}
              />
              <Button
                variant="secondary"
                text="Cancel"
                onClick={handleCancelClick}
                disabled={uploading || creating}
              />
            </Slider.Actions>
          )}

        </Slider.Dialog>
      )}
    </>
  );
}


interface BannerPositionMenuProps {
  handlePositionChange: (value: string) => void;
}

const BannerPositionMenu: React.FC<BannerPositionMenuProps> = ({ handlePositionChange }) => {
  const transitions = useTransition(true, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 500 },
    delay: 500,
  });

  return transitions((style, item) =>
    item ? (
      <animated.div style={style} className={styles.positionMenu}>
        <Actions.Container>
          <Actions.Row className={styles.positionActionRow}>
            <Actions.Item className={styles.positions}>
              {POSITIONS.map((position) => {
                return (
                  <div
                    key={position.value}
                    className={cx(styles.position)}
                    onClick={() => handlePositionChange(position.value)}
                  />
                );
              })}
            </Actions.Item>
          </Actions.Row>
        </Actions.Container>
      </animated.div>
    ) : null
  );
};